import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'

interface Environment {
  id: string
  name: string
  config: {
    type: string
    networking?: { type: string; allowed_hosts?: string[] }
    packages?: Record<string, string[]>
  }
  created_at: string
  archived_at: string | null
}

interface Props {
  environments: Environment[]
  api_error?: string
  flash?: { success?: string; error?: string }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function Environments({ environments, api_error }: Props) {
  const [actionId, setActionId] = useState<string | null>(null)

  const handleArchive = (envId: string, envName: string) => {
    if (!confirm(`Archive environment "${envName}"? Existing sessions continue, but new sessions cannot use it.`)) return
    setActionId(envId)
    router.post(`/admin/managed_agents/environments/${envId}/archive`, {}, {
      onFinish: () => setActionId(null),
    })
  }

  const handleDelete = (envId: string, envName: string) => {
    if (!confirm(`Permanently delete environment "${envName}"? Only works if no sessions reference it.`)) return
    setActionId(envId)
    router.delete(`/admin/managed_agents/environments/${envId}`, {
      onFinish: () => setActionId(null),
    })
  }

  const activeEnvs = environments.filter(e => !e.archived_at)
  const archivedEnvs = environments.filter(e => e.archived_at)
  const sorted = [...activeEnvs, ...archivedEnvs]

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Managed Agents', href: '/admin/managed_agents' }, { label: `Environments (${environments.length})` }]} />

      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Link href="/admin/managed_agents">
            <Button variant="outline" size="sm">Agents</Button>
          </Link>
          <Button variant="default" size="sm" disabled>Environments</Button>
        </div>

        {api_error && (
          <Card className="mb-4 border-red-200 dark:border-red-800">
            <CardContent className="py-3 text-sm text-red-600 dark:text-red-400">
              API Error: {api_error}
            </CardContent>
          </Card>
        )}

        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Type</th>
                <th className="text-left px-4 py-3 font-medium">Networking</th>
                <th className="text-left px-4 py-3 font-medium">Packages</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Created</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    {api_error ? 'Could not load environments' : 'No environments found'}
                  </td>
                </tr>
              )}
              {sorted.map(env => {
                const packageCount = env.config?.packages
                  ? Object.values(env.config.packages).flat().length
                  : 0

                return (
                  <tr key={env.id} className="border-b border-border hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <span className="font-medium">{env.name}</span>
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">{env.id}</p>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{env.config?.type || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs">{env.config?.networking?.type || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{packageCount > 0 ? `${packageCount} packages` : '—'}</td>
                    <td className="px-4 py-3">
                      {env.archived_at ? (
                        <Badge variant="secondary">Archived</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(env.created_at)}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      {!env.archived_at && (
                        <button
                          onClick={() => handleArchive(env.id, env.name)}
                          disabled={actionId === env.id}
                          className="text-amber-600 hover:underline text-xs disabled:opacity-50"
                        >
                          Archive
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(env.id, env.name)}
                        disabled={actionId === env.id}
                        className="text-red-600 hover:underline text-xs disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

Environments.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Environments
